import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Edit, Delete, Save, Science, CheckCircle, History } from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { extractExamsProceduresHistory } from "@/fluxvet/utils/questionnaireResponseHelper";
import { HistoryModal } from "./HistoryModal";

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

interface ExamProcedure {
  id: string;
  description: string;
  notes: string;
}

interface ExamsProceduresSectionProps {
  control: any;
  watch: any;
  setValue: any;
}

export const ExamsProceduresSection: React.FC<ExamsProceduresSectionProps> = ({
  control,
  watch,
  setValue,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair exames/procedimentos solicitados
  const examsProceduresHistory = useMemo(
    () => extractExamsProceduresHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const examsOrProcedures: ExamProcedure[] = watch("examsOrProcedures") || [];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<ExamProcedure, "id">>({
    description: "",
    notes: "",
  });

  // Estado para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
  });

  const handleAdd = () => {
    if (!formData.description.trim()) return;
    const newItem: ExamProcedure = { id: generateId(), ...formData };
    setValue("examsOrProcedures", [...examsOrProcedures, newItem]);
    resetForm();
  };

  const handleEdit = (item: ExamProcedure) => {
    // Não permite editar item já assinado
    if ((item as any).signed) {
      alert("Este item já foi assinado digitalmente e não pode ser editado.");
      return;
    }
    
    setEditingId(item.id);
    setFormData({ description: item.description, notes: item.notes });
  };

  const handleSaveEdit = () => {
    if (!formData.description.trim()) return;
    const updated = examsOrProcedures.map((item) =>
      item.id === editingId ? { ...item, ...formData } : item
    );
    setValue("examsOrProcedures", updated);
    resetForm();
  };

  const handleDelete = (id: string) => {
    // Verifica se o item está assinado
    const item = examsOrProcedures.find((e) => e.id === id);
    if (item && (item as any).signed) {
      alert("Este item já foi assinado digitalmente e não pode ser removido.");
      return;
    }
    
    setValue(
      "examsOrProcedures",
      examsOrProcedures.filter((item) => item.id !== id)
    );
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ description: "", notes: "" });
  };

  return (
    <>
    <Box>
      {/* Cabeçalho */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
        <Science sx={{ color: "#00A884" }} />
        <Typography variant="h6" fontWeight={600}>
          Exames e Procedimentos
        </Typography>
        {examsOrProcedures.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            ({examsOrProcedures.length}{" "}
            {examsOrProcedures.length === 1 ? "item" : "itens"})
          </Typography>
        )}
          </Stack>
          {examsProceduresHistory.length > 0 && (
            <Button
              size="small"
              startIcon={<History />}
              onClick={() => setHistoryModal({ isOpen: true })}
              sx={{
                textTransform: "none",
                fontSize: "0.85rem",
                color: "#666",
                "&:hover": {
                  color: "#00A884",
                  bgcolor: "rgba(0, 168, 132, 0.08)",
                },
              }}
            >
              Ver histórico de exames
            </Button>
          )}
      </Stack>

      <Grid container spacing={2}>
        {/* Coluna Esquerda: Formulário (40%) */}
        <Grid item xs={12} md={4.8}>
          <Paper
            key={`exam-form-${examsOrProcedures.length}`}
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              bgcolor: "#fafafa",
              height: "100%",
              minHeight: 420,
            }}
          >
            <Stack spacing={2} height="100%">
              <Typography variant="subtitle2" fontWeight={600} color="#00A884">
                {editingId ? "✏️ Editando" : "➕ Novo item"}
              </Typography>

              <Divider />

              <TextField
                fullWidth
                label="Descrição do exame ou procedimento *"
                placeholder="Ex: Hemograma completo"
                value={editingId ? formData.description : undefined}
                defaultValue=""
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Nota ou observação"
                placeholder="Ex: Realizar em jejum de 12 horas"
                value={editingId ? formData.notes : undefined}
                defaultValue=""
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                multiline
                rows={4}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                  },
                }}
              />

              <Box sx={{ flexGrow: 1 }} />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {editingId && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{ textTransform: "none" }}
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={editingId ? <Save /> : <Add />}
                  onClick={editingId ? handleSaveEdit : handleAdd}
                  sx={{
                    bgcolor: "#00A884",
                    "&:hover": { bgcolor: "#008c6e" },
                    textTransform: "none",
                  }}
                >
                  {editingId ? "Salvar" : "Adicionar"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Coluna Direita: Lista de itens (60%) */}
        <Grid item xs={12} md={7.2}>
          {examsOrProcedures.length > 0 ? (
            <Stack spacing={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight={600}
              >
                ITENS SOLICITADOS
              </Typography>
              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#00A884",
                    borderRadius: "10px",
                  },
                }}
              >
                <Stack spacing={2}>
                  {examsOrProcedures.map((item) => {
                    const isSigned = (item as any).signed;
                    
                    return (
                      <Card
                        key={item.id}
                        sx={{
                          border: isSigned ? "2px solid #00A884" : "1px solid #e0e0e0",
                          transition: "all 0.2s ease",
                          bgcolor: isSigned ? "rgba(0, 168, 132, 0.02)" : "transparent",
                          "&:hover": {
                            borderColor: "#00A884",
                            boxShadow: "0 4px 12px rgba(0, 168, 132, 0.15)",
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography
                                  variant="h6"
                                  fontWeight={600}
                                  color="#00A884"
                                >
                                  {item.description}
                                </Typography>
                                {isSigned && (
                                  <CheckCircle sx={{ fontSize: 20, color: "#00A884" }} />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(item)}
                                  disabled={isSigned}
                                  sx={{
                                    color: isSigned ? "#cccccc" : "#666",
                                    "&:hover": {
                                      color: isSigned ? "#cccccc" : "#00A884",
                                      bgcolor: isSigned ? "transparent" : "rgba(0, 168, 132, 0.08)",
                                    },
                                  }}
                                >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item.id)}
                                disabled={isSigned}
                                sx={{
                                  color: isSigned ? "#cccccc" : "#666",
                                  "&:hover": {
                                    color: isSigned ? "#cccccc" : "#f44336",
                                    bgcolor: isSigned ? "transparent" : "rgba(244, 67, 54, 0.08)",
                                  },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                          {item.notes && (
                            <>
                              <Divider />
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={600}
                                >
                                  Observações:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "pre-wrap" }}
                                >
                                  {item.notes}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "#fafafa",
                border: "1px dashed #e0e0e0",
                borderRadius: 2,
                minHeight: 420,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Science sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum exame ou procedimento adicionado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Preencha os campos ao lado e clique em "Adicionar"
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, fontStyle: "italic" }}
              >
                💡 Dica: Use a tecla Tab para navegar entre os campos
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>

    {/* Modal de histórico — só exibido quando há histórico */}
    {examsProceduresHistory.length > 0 && (
      <HistoryModal
        open={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false })}
        title="Histórico - Exames e Procedimentos Solicitados"
        history={examsProceduresHistory}
      />
    )}
  </>
  );
};

export default ExamsProceduresSection;
